<template>
    <b-modal id="detailsModal" title="Shift Details" v-model="showModal" size="lg">
        <b-container fluid v-if="shift.id">
            <b-row class="with-padding-bottom">
                <b-col sm="6">
                    <strong>Claimant</strong>
                    <br />
                    {{ shift.client_name }}
                </b-col>
                <b-col sm="6">
                    <strong>Provider</strong><br />
                    {{ shift.caregiver_name }}
                </b-col>
            </b-row>
            <b-row class="with-padding-bottom">
                <b-col sm="6">
                    <strong>Checked In Time</strong><br />
                    {{checkInTime}}
                </b-col>
                <b-col sm="6">
                    <strong>Checked Out Time</strong><br />
                      {{checkOutTime}}
                   <br />
                </b-col>
            </b-row>
            <b-row>
                <b-col sm="6" class="with-padding-bottom">
                    <strong>Shift Type</strong><br>
                    {{ hoursType(shift)}}
                </b-col>
            </b-row>
            <b-row class="with-padding-bottom" v-if="shift.schedule && shift.schedule.notes">
                <b-col sm="12">
                    <strong>Schedule Notes</strong><br />
                    {{ shift.schedule.notes }}
                </b-col>
            </b-row>
            <b-row class="with-padding-bottom">
                <b-col sm="6">
                    <strong>Did the client fall?</strong><br />
                    <span v-if="shift.client_fall">Yes</span>
                    <span v-else>No</span>
                </b-col>
                <b-col sm="6">
                    <strong>Client's Condition</strong><br />
                    <span v-if="shift.client_condition > 0">Improved</span>
                    <span v-else-if="shift.client_condition < 0">Declined</span>
                    <span v-else>No Change</span>
                </b-col>
            </b-row>
            <b-row class="with-padding-bottom" v-if="shift.client.carrier.hospital_stays_enabled">
              <b-col sm="6 py-2">
                <strong>Has the client had any hospital stays since your last visit?</strong><br />
                <span v-if="shift.hospital_stay">Yes</span>
                <span v-else>No</span>
              </b-col>
              <b-col sm="6 py-2" v-if="shift.client.carrier.hospital_stays_enhanced_flow && shift.hospital_stay">
                <strong>Is the client still currently in the hospital?</strong><br />
                <span v-if="shift.still_in_hospital">Yes</span>
                <span v-else>No</span>
              </b-col>

              <b-col sm="6 py-2" v-if="shift.client.carrier.hospital_stays_enhanced_flow && shift.hospital_stay">
                <strong>Hospital stay dates</strong><br />
                <span>
                    {{convertDateYmdToMdy(shift.hospital_stay_from_date)}}
                  &nbsp; to &nbsp;
                  <span v-if="shift.still_in_hospital">Currently in the hospital</span>
                  <span v-else> {{convertDateYmdToMdy(shift.hospital_stay_to_date)}}</span>
                </span>
              </b-col>
            </b-row>

            <b-row class="with-padding-bottom" v-if="shift.modification_reasons && shift.modification_reasons.length > 0">
                <b-col sm="6 py-2">
                    <strong>What are the reasons for modification?</strong><br />
                    <span>{{ printModificationReasons }}</span>
                </b-col>
            </b-row>

            <b-row class="with-padding-bottom" v-if="shift.manual_addition_reason">
                <b-col sm="6 py-2">
                    <strong>What is the reason for the manual addition?</strong><br />
                    <span>{{ shift.manual_addition_reason }}</span>
                </b-col>
            </b-row>
            
            <b-row v-if="shift.checked_out_time !== 'Pending'">
                <b-col md="6">
                    <b-row class="with-padding-bottom">
                        <b-col sm="12">
                            <strong>Provider Comments</strong><br />
                            {{ shift.caregiver_comments ? shift.caregiver_comments : 'No comments recorded' }}
                        </b-col>
                    </b-row>

                    <b-row class="with-padding-bottom" v-if="shift.client.client_type == 'LTCI' && shift.signature != null">
                        <b-col>
                            <strong>Claimant Signature</strong>
                            <div v-html="shift.signature.content" class="signature"></div>
                        </b-col>
                    </b-row>

                    <strong>Activities Performed</strong>
                    <b-row>
                        <b-col sm="12">
                            <p v-if="!shift.activities || !shift.activities.length">
                                No activities recorded
                            </p>
                            <table class="table table-sm" v-else>
                                <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Detail</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="activity in shift.activities" :key="activity.id">
                                    <td>{{ activity.name }}</td>
                                    <td>{{ activity.value }}</td>
                                </tr>
                                </tbody>
                            </table>
                        </b-col>
                    </b-row>

                    <strong>Was this Shift Electronically Verified?</strong>
                    <b-row class="with-padding-bottom">
                        <b-col sm="6">
                            <strong style="color: darkgreen" v-if="shift.verified">Yes</strong>
                            <strong style="color: darkred" v-else>No</strong>
                        </b-col>
                        <b-col sm="12" v-if="shift.evv_message">
                            <small>EVV Message: {{ shift.evv_message }}</small>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col md="6">
                    <div v-if="shift.flags.includes('deleted')" class="mb-2">
                        <i class="fa fa-times-circle" v-b-tooltip.hover title="Deleted"></i>
                        <strong>Deleted</strong>
                    </div>
                    <div v-if="shift.flags.includes('added')" class="mb-2">
                        <i class="fa fa-plus-circle" v-b-tooltip.hover title="Manually Added"></i>
                        <strong>Manually Added</strong>
                    </div>
                    <div v-if="shift.flags.includes('time_excessive')" class="mb-2">
                        <i class="fa fa-clock-o" v-b-tooltip.hover title="Excessive Visit Length"></i>
                        <strong>Excessive Visit Length</strong>
                    </div>
                    <div v-if="shift.flags.includes('time_expire')" class="mb-2">
                        <i class="fa fa-clock-o" v-b-tooltip.hover title="Time Expired"></i>
                        <strong>Time Expired</strong>
                    </div>
                    <div v-if="shift.flags.includes('disputed')" class="mb-2">
                        <i class="fa fa-exclamation-triangle" v-b-tooltip.hover title="Disputed"></i>
                        <strong>Disputed</strong>
                        <div v-if="shift.dispute">
                            Disputed Reason: {{ shift.dispute.reason ? shift.dispute.reason.reason : '' }}
                        </div>
                        <div v-if="shift.dispute">
                            Instructions to Claimant:  {{ shift.dispute.notes }}
                        </div>
                    </div>
                    <div v-if="shift.flags.includes('modified')" class="mb-2">
                        <i class="fa fa-pencil" v-b-tooltip.hover title="Modified"></i>
                        <strong>Modified</strong>
                        <div v-for="(diff, index) in shift.change_log" :key="index">
                            <div class="text-muted">
                                <span v-if="diff.revisor">by {{ diff.revisor.name }}</span>
                                at
                                <utc-time timezone="America/Chicago" :date="diff.date" format="hh:mm a z MM/DD/YYYY" />
                            </div>
                            <div v-for="(change, i) in diffMap(diff.changes)" :key="i">
                                <strong>{{ change[0] }}</strong>: {{ change[1] }}
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <b-row v-if="shift.address">
                <b-col sm="12" md="6">
                    <shift-map
                        :serviceAddress="shift.address"
                        :start="{latitude: shift.checked_in_latitude, longitude: shift.checked_in_longitude}"
                        :end="{latitude: shift.checked_out_latitude, longitude: shift.checked_out_longitude}"
                        :logs="shift.mobile_tracking_logs"
                        :evv-radius="evvRadius"
                        class="shift-map"
                    />
                </b-col>
                <b-col sm="12" md="6">
                    <b-row>
                        <b-col sm="12">
                            <table class="table table-sm">
                                <thead>
                                <tr>
                                    <th colspan="2">Check In</th>
                                </tr>
                                </thead>
                                <tbody v-if="shift.checked_in_latitude || shift.checked_in_longitude">
                                <tr>
                                    <th>Geocode</th>
                                    <td>{{ shift.checked_in_latitude.slice(0,8) }}, {{ shift.checked_in_longitude.slice(0,8) }}</td>
                                </tr>
                                <tr>
                                    <th>Distance</th>
                                    <td>{{ shift.checked_in_distance }}m</td>
                                </tr>
                                </tbody>
                                <tbody v-else-if="shift.checked_in_number">
                                <tr>
                                    <th>Phone Number</th>
                                    <td>{{ phoneFormat(shift.checked_in_number) }}</td>
                                </tr>
                                <tr v-if="shift.checked_in_expected_number">
                                    <th>Expected Number</th>
                                    <td>{{ phoneFormat(shift.checked_in_expected_number) }}</td>
                                </tr>
                                </tbody>
                                <tbody v-else>
                                <tr>
                                    <td colspan="2">No EVV data</td>
                                </tr>
                                </tbody>
                            </table>
                        </b-col>
                    </b-row>
                    <b-row v-if="shift.checked_out_time && shift.checked_out_time !== 'Pending'">
                        <b-col sm="12">
                            <table class="table table-sm">
                                <thead>
                                <tr>
                                    <th colspan="2">Check Out</th>
                                </tr>
                                </thead>
                                <tbody v-if="shift.checked_out_latitude || shift.checked_out_longitude">
                                    <tr>
                                        <th>Geocode</th>
                                        <td>{{ shift.checked_out_latitude.slice(0,8) }}, {{ shift.checked_out_longitude.slice(0,8) }}</td>
                                    </tr>
                                    <tr>
                                        <th>Distance</th>
                                        <td>{{ shift.checked_out_distance }}m</td>
                                    </tr>
                                </tbody>
                                <tbody v-else-if="shift.checked_out_number">
                                    <tr>
                                        <th>Phone Number</th>
                                        <td>{{ phoneFormat(shift.checked_out_number) }}</td>
                                    </tr>
                                    <tr v-if="shift.checked_out_expected_number">
                                        <th>Expected Number</th>
                                        <td>{{ phoneFormat(shift.checked_out_expected_number) }}</td>
                                    </tr>
                                </tbody>
                                <tbody v-else>
                                    <tr>
                                        <td colspan="2">No EVV data</td>
                                    </tr>
                                </tbody>
                            </table>
                        </b-col>
                    </b-row>
                    <b-row v-if="shift.mobile_tracking_logs && shift.mobile_tracking_logs.length">
                        <b-col sm="12">
                            <table class="table table-sm">
                                <thead>
                                <tr>
                                    <th colspan="2">Shift Pings</th>
                                </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th>Number of Pings</th>
                                        <td>{{ shift.mobile_tracking_logs.length }}</td>
                                    </tr>
                                    <tr>
                                        <th>% of pings within {{ evvRadius }}m</th>
                                        <td>{{ shift.ping_percent_verified }}%</td>
                                    </tr>
                                    <tr>
                                        <th>% of pings outside {{ evvRadius }}m</th>
                                        <td>{{ shift.ping_percent_unverified }}%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
        </b-container>
        <div slot="modal-footer">
            <b-btn variant="default" @click="downloadSelected()" v-if=" !isCarrierManager "><i class="fa fa-file-pdf-o"></i> Download PDF</b-btn>
            <b-btn variant="primary" @click="printSelected()" v-if=" !isCarrierManager "><i class="fa fa-print"></i> Print</b-btn>
            <b-btn variant="default" @click="showModal=false">Close</b-btn>
        </div>
    </b-modal>
</template>

<script>
    import FormatsNumbers from '../../mixins/FormatsNumbers';
    import FormatsDates from '../../mixins/FormatsDates';
    import ShiftDiffs from '../../mixins/ShiftDiffs';
    import UsesRole from '../../mixins/UsesRole';
    export default {
        mixins: [FormatsNumbers, FormatsDates, ShiftDiffs, UsesRole],

        props: {
            value: {},
            shift: {},
            items: Array,
        },

        computed: {
            showModal: {
                get() {
                    return this.value;
                },
                set(value) {
                    this.$emit('input', value);
                }
            },
            evvRadius: {
                get() {
                    if (this.shift && this.shift.client && this.shift.client.carrier && this.shift.client.carrier.evv_radius) {
                        return this.shift.client.carrier.evv_radius;
                    }
                    return 300;
                },
            },
            printModificationReasons() {
                let data = []

                this.shift.modification_reasons.forEach(item => { 
                    switch (item) {
                        case 1:
                            data.push("Hourly rate incorrect needs change")
                            break;
                        case 2:
                            data.push("Caregiver forgot to clock out")
                            break;
                        case 3:
                            data.push("Caregiver clocked in/out at the wrong time")
                            break;
                        case 4:
                            data.push("Caregiver needs to add/modify ADLs")
                            break;
                        case 5:
                            data.push("Caregiver needs to provide additional information")
                            break;
                        default:
                            data.push(item)
                            break;
                    }

                })
                return data.join(", ");
            },
            checkInTime(){
                if(this.shift && this.shift.checked_in_time){
                  let format = 'h:mm A MM/DD/YYYY zz';
                  let tz = 'UTC';
                 if(this.shift.checked_in_timezone){
                   tz = this.shift.checked_in_timezone;
                    } else if (this.shift.shift_timezone) {
                      tz = this.shift.shift_timezone;
                    }
                  return moment.utc(this.shift.checked_in_time).tz(tz).format(format);
                }
                else{
                  return '';
                }
            },
            checkOutTime(){
            if(this.shift && this.shift.checked_out_time){
              let format = 'h:mm A MM/DD/YYYY zz';
              let tz = 'UTC';
                if(this.shift.checked_out_timezone){
                     tz = this.shift.checked_out_timezone;
                    } else if (this.shift.shift_timezone) {
                    tz = this.shift.shift_timezone;
                    }
              return moment.utc(this.shift.checked_out_time).tz(tz).format(format);
            }
            else{
              return '';
            }
          },
        },

        methods: {
            hoursType(item) {
                switch (item.hours_type) {
                    case 'default':
                        return 'Regular';
                    case 'overtime':
                        return 'OT';
                    case 'holiday':
                        return 'HOL';
                }
            },

            printSelected() {
                $("#detailsModal .container-fluid").print();
            },

            downloadSelected() {
                let url = `/${this.controller}/shifts/${this.shift.id}/print?type=pdf`;
                window.location = url;
            },

            titleCase(str) {
                return str.toLowerCase()
                    .split(' ')
                    .map(function(word) {
                        return word.replace(word[0], word[0].toUpperCase());
                    })
                    .join(' ');
            },

            readableKey(key) {
                key = key.replace('client', 'claimant');
                key = key.replace('caregiver', 'provider');
                key = key.replace('shift', 'visit');
                key = key.replace('_', ' ');
                return this.titleCase(key);
            }
        }
    }
</script>

<style>
    table {
        font-size: 14px;
    }
    .table-info, .table-info>td, .table-info>th {
        font-weight: bold;
        font-size: 13px;
        background-color: #ecf7f9;
    }
    .table-sm td,
    .table-sm th {
        padding: 0.2rem 0;
    }
    .signature > svg {
        margin: -25px 0;
        width: 100%;
        height: auto;
        max-width: 400px;
    }
    .shift-map {
        width: 100%;
        height: 350px;
    }
</style>
